import { cn } from "@/lib/utils"

/**
 * FloorCore Measure identity — a green plan boundary with a measured corner,
 * paired with the "Floor·Core" wordmark and a MEASURE eyebrow.
 *
 * The corner strokes `currentColor` instead of a fixed navy, so the dark theme
 * lands on the design's "reversed" treatment (white corner on navy) for free.
 * The frame and dimension ticks reference `--color-accent` directly rather than
 * a Tailwind utility: the mark must render even if a content-scan hiccup drops
 * an emitted class, and an unstroked mark is an invisible one.
 */

type MarkGeometry = {
  /** Frame inset, span, and corner radius, in the 32-unit viewBox. */
  inset: number
  span: number
  rx: number
  stroke: number
  corner: string
  ticks: boolean
}

/**
 * The mark simplifies as it shrinks, per the design's scale rules: the
 * dimension ticks drop below 32px and the strokes thicken so the corner still
 * reads in the nav rail and the browser tab.
 */
function geometryFor(size: number): MarkGeometry {
  if (size >= 32) {
    return {
      inset: 3.4,
      span: 25.2,
      rx: 6.4,
      stroke: 2.4,
      corner: "M10.6 9.8 V 21.4 H 22.2",
      ticks: true,
    }
  }
  if (size >= 20) {
    return {
      inset: 3,
      span: 26,
      rx: 6.4,
      stroke: 3,
      corner: "M10.6 9.8 V 21.4 H 22.2",
      ticks: false,
    }
  }
  return {
    inset: 2.6,
    span: 26.8,
    rx: 6.4,
    stroke: 4,
    corner: "M11 10 V 21 H 22",
    ticks: false,
  }
}

export function LogoMark({
  size = 24,
  className,
  title,
}: {
  size?: number
  className?: string
  /** Only set this when the mark stands alone — otherwise the adjacent
   *  wordmark already names it and a second label just repeats itself. */
  title?: string
}) {
  const g = geometryFor(size)
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      className={cn("shrink-0", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      // The mark carries no text, and it is aria-hidden whenever a wordmark
      // sits beside it — so system tests have nothing else to hook onto.
      data-testid="logo-mark"
    >
      <rect
        x={g.inset}
        y={g.inset}
        width={g.span}
        height={g.span}
        rx={g.rx}
        stroke="var(--color-accent)"
        strokeWidth={g.stroke}
      />
      <path
        d={g.corner}
        stroke="currentColor"
        strokeWidth={g.stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {g.ticks && (
        <path
          d="M15 21.4 V 17.6 M19 21.4 V 17.6"
          stroke="var(--color-accent)"
          strokeWidth={1.7}
          strokeLinecap="round"
        />
      )}
    </svg>
  )
}

export function Logo({
  layout = "inline",
  className,
}: {
  /** `inline` is the nav-rail lockup; `stacked` is the full sign-in lockup. */
  layout?: "inline" | "stacked"
  className?: string
}) {
  if (layout === "stacked") {
    return (
      <span className={cn("flex items-center gap-4 text-ink-display", className)}>
        <LogoMark size={44} />
        <span className="flex flex-col gap-[3px]">
          <span className="font-display text-[28px] font-bold leading-none tracking-[-0.02em]">
            Floor<span className="text-accent">Core</span>
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted">
            Measure
          </span>
        </span>
      </span>
    )
  }

  // "Measure" sits a step down from "FloorCore" rather than matching it as the
  // design's rail cell does: that cell had the full 200px rail to itself,
  // whereas here the collapse chevron takes 44px of it. At matched sizes the
  // wordmark needs 128px against 126 available once the webfont is missing —
  // which is exactly the state system tests and the pre-swap first paint
  // render in, so it truncated to "FloorCore Meas…" there.
  return (
    <span
      className={cn("flex min-w-0 items-center gap-2 text-ink-display", className)}
    >
      <LogoMark size={22} />
      <span className="truncate font-display text-sm font-semibold">
        Floor<span className="text-accent">Core</span>{" "}
        <span className="text-xs font-medium text-ink-muted">Measure</span>
      </span>
    </span>
  )
}
